import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { databasesApi } from '../services/api';
import { setTabDatabase, rememberDatabaseList } from '../services/activeDatabase';

const DatabaseContext = createContext();

export const useDatabase = () => {
  const context = useContext(DatabaseContext);
  if (!context) {
    throw new Error('useDatabase must be used within a DatabaseProvider');
  }
  return context;
};

/**
 * Safe version that returns null instead of throwing when outside DatabaseProvider.
 * Used by components that render in both data pages (with DB) and admin pages (without).
 */
export const useDatabaseSafe = () => {
  return useContext(DatabaseContext);
};

/**
 * Custom navigate hook that auto-prefixes paths with the current DB slug.
 * Usage: const dbNavigate = useDbNavigate();
 *        dbNavigate('buildings');        // → /gwa/buildings
 *        dbNavigate('buildings/123');     // → /gwa/buildings/123
 */
export const useDbNavigate = () => {
  const navigate = useNavigate();
  const { dbSlug } = useDatabase();

  return useCallback((path, options) => {
    // Strip leading slash if present
    const cleanPath = path.startsWith('/') ? path.slice(1) : path;
    navigate(`/${dbSlug}/${cleanPath}`, options);
  }, [navigate, dbSlug]);
};

export const DatabaseProvider = ({ children }) => {
  const { dbSlug } = useParams();
  const navigate = useNavigate();
  const [availableDatabases, setAvailableDatabases] = useState([]);
  const [currentDb, setCurrentDb] = useState(null); // Full db object
  const [loading, setLoading] = useState(true);

  // Fetch available databases
  useEffect(() => {
    databasesApi.getAvailable()
      .then(res => {
        const dbs = res.data || [];
        setAvailableDatabases(dbs);

        // Resolve URL :dbSlug param to a database.
        // Match on db.slug (the immutable Slug column). Old URLs derived from displayName
        // (e.g. /gwa/... when slug is gwa01) will fall through and get redirected to /select-database.
        const matched = dbs.find(d => d.slug === dbSlug?.toLowerCase());

        if (matched) {
          setCurrentDb(matched);
          // This tab's database (activeDatabase.js), which also becomes the last pick. Report
          // file names lead with the client's name; the exporters are plain functions, so they
          // read it from there like the api.js interceptor reads the database.
          // setTabDatabase notifies this tab's listeners when its database actually changes.
          const dbName = matched.databaseName || matched.id;
          const clientName = matched.organizationName || matched.displayName || '';
          setTabDatabase({ databaseName: dbName, slug: dbSlug, clientName });
        } else {
          // Invalid slug — redirect to database picker
          navigate('/select-database', { replace: true });
          return;
        }

        setLoading(false);
      })
      .catch(() => {
        // Fallback if backend unavailable
        const fallbackDbs = [
          { id: 'pmd-preprod-db-gwa01', name: 'GWA', displayName: 'GWA', databaseName: 'pmd-preprod-db-gwa01', slug: 'gwa01', description: 'Global Wafers' },
          { id: 'pmd-preprod-db-gfs01', name: 'GFS', displayName: 'GFS', databaseName: 'pmd-preprod-db-gfs01', slug: 'gfs01', description: 'GlobalFoundries' },
          { id: 'pmd-preprod-db-pna01', name: 'PNA', displayName: 'PNA', databaseName: 'pmd-preprod-db-pna01', slug: 'pna01', description: 'Panasonic' }
        ];
        setAvailableDatabases(fallbackDbs);
        rememberDatabaseList(fallbackDbs);
        const matched = fallbackDbs.find(d => d.slug === dbSlug?.toLowerCase());
        if (matched) {
          setCurrentDb(matched);
          setTabDatabase({ databaseName: matched.databaseName, slug: matched.slug, clientName: matched.displayName });
        }
        setLoading(false);
      });
  }, [dbSlug]); // eslint-disable-line react-hooks/exhaustive-deps

  // Switch to a different database — navigates using the db's slug.
  // Accepts either a db object (preferred) or a raw slug string for backward compat.
  const switchDatabase = useCallback((dbOrSlug) => {
    const newSlug = typeof dbOrSlug === 'string' ? dbOrSlug : dbOrSlug?.slug;
    if (!newSlug) return;
    navigate(`/${newSlug}/`);
  }, [navigate]);

  const dbDisplayName = currentDb?.displayName || currentDb?.name || dbSlug?.toUpperCase() || '';

  const value = {
    currentDb,
    dbSlug: dbSlug?.toLowerCase() || '',
    dbDisplayName,
    availableDatabases,
    switchDatabase,
    loading
  };

  return (
    <DatabaseContext.Provider value={value}>
      {children}
    </DatabaseContext.Provider>
  );
};

export default DatabaseContext;
